// ============================================================================
// JARVISTRAVEL - DATA MAPPERS
// Version: 1.0.0
// Transform backend snake_case responses into frontend camelCase types
// ============================================================================

import { warnOnce } from "@/app/utils/observability";
import { ymdFrom } from '@/app/utils/dateOnly';
import { apiUrl } from "@/app/api/client";
import type {
  TripDetailResponse,
  DaySummary,
  BudgetSummary,
  FatigueSummary,
  ActivitySummary,
  CollaboratorSummary,
  TripStatsSummary,
  WeatherSummary,
} from "./trips";

import type {
  AccommodationResponse,
  PropertyDetailResponse,
} from "./accommodations";

import type {
  Trip,
  TripAccess,
  JournalTrip,
  Budget,
  BudgetCategory,
  Transaction,
  FatigueIndex,
  FatigueImpact,
  FatigueLevel,
  FatigueComponent,
  FactorContribution,
  TripStatus,
  ItineraryDay,
  Activity,
  Collaborator,
  TripStats,
  Weather,
  Property,
  StayCost,
} from "@/app/types";

// ---------------------------------------------------------------------------
// Trip Detail → frontend Trip
// ---------------------------------------------------------------------------

export function mapTripDetail(detail: TripDetailResponse): JournalTrip {
  const totalTravelers = (detail.adults_count || 1) + (detail.children_count || 0);

  const trip: JournalTrip = {
    id: detail.id,
    name: detail.name,
    destination: detail.destination,
    country: detail.country,
    image: detail.image_url || "",
    dates: {
      checkin: ymdFrom(detail.start_date),
      checkout: ymdFrom(detail.end_date),
    },
    status: mapStatus(detail.status || "planning"),
    travelers: {
      adults: detail.adults_count || 1,
      children: detail.children_count || 0,
    },
    travelerNames: detail.traveler_names,
    // Always set from the detail read, "" when core has none: the notes are
    // known. A trip from list data leaves them undefined, unknown (JAR-1973).
    plannerNotes: detail.planner_notes ?? '',
    // null means no limit applies, which the app reads as "no count to show".
    ...(detail.date_changes_left !== null && detail.date_changes_left !== undefined
      ? { dateChangesLeft: detail.date_changes_left }
      : {}),
    ...(detail.planning_closes_at ? { planningClosesAt: detail.planning_closes_at } : {}),
    access: mapAccess(detail.access),
    budget: mapBudget(detail.budget, totalTravelers),
    fatigueIndex: mapFatigue(detail.fatigue_index),
    itinerary: mapItinerary(detail.itinerary, detail.budget?.currency || "USD"),
    collaborators: mapCollaborators(detail.collaborators),
    stats: mapStats(detail.stats),
    weather: mapWeather(detail.weather),
    plannedActivities: extractPlannedActivities(detail.itinerary),
    createdAt: detail.created_at || new Date().toISOString(),
    updatedAt: detail.updated_at || new Date().toISOString(),
  };

// ---------------------------------------------------------------------------
// Planned activities helper (derived from itinerary)
// ---------------------------------------------------------------------------

function extractPlannedActivities(itinerary: DaySummary[] | undefined): string[] {
  if (!itinerary || itinerary.length === 0) return [];

  const seen = new Set<string>();
  const activities: string[] = [];

  for (const day of itinerary) {
    if (!day.activities) continue;
    for (const act of day.activities) {
      if (act.name && !seen.has(act.name)) {
        seen.add(act.name);
        activities.push(act.name);
        if (activities.length >= 10) return activities;
      }
    }
  }

  return activities;
}

  return trip;
}

// ---------------------------------------------------------------------------
// Status mapper
// ---------------------------------------------------------------------------

function mapStatus(backendStatus: string): TripStatus {
  switch (backendStatus) {
    case "completed":
    case "Completed":
      return "Completed";
    case "in_progress":
    case "in-progress":
    case "active":
    case "Active":
      return "Active";
    case "planning":
    case "in_planning":
    case "in-planning":
    case "upcoming":
    case "new":
    case "Draft":
      return "Draft";
    case "Archived":
      return "Archived";
    default:
      return "Draft";
  }
}

// ---------------------------------------------------------------------------
// Budget mapper
// ---------------------------------------------------------------------------

function mapBudget(b: BudgetSummary | undefined, totalTravelers: number): Budget {
  if (!b) {
    return {
      total: 0, spent: 0, remaining: 0, currency: "USD",
      perPerson: 0, categories: [], transactions: [],
    };
  }

  const categories: BudgetCategory[] = (b.categories || []).map((c) => ({
    // `id` is the category code the rest of the FE joins on; `backendId` is the
    // row UUID the API layer needs to write an allocation back (JAR-791).
    id: c.name,
    backendId: c.id,
    name: c.name,
    icon: c.icon || getCategoryIcon(c.name),
    color: c.color || "#5C6B77", // Meridian muted — the data-set fallback
    allocated: c.allocated,
    spent: c.spent,
  }));

  const transactions: Transaction[] = (b.transactions || []).map((t) => ({
    id: t.id,
    description: t.description,
    amount: t.amount,
    category: t.category,
    date: t.date,
    notes: t.notes || undefined,
    // Anything but card or cash reads as unset: the icon can draw only those two.
    method: t.payment_method === 'card' || t.payment_method === 'cash' ? t.payment_method : undefined,
    currency: t.currency || undefined,
    source: t.source,
  }));

  return {
    total: b.total,
    spent: b.spent,
    remaining: b.remaining,
    currency: b.currency || "USD",
    perPerson: totalTravelers > 0 ? b.total / totalTravelers : 0,
    categories,
    transactions,
  };
}

function getCategoryIcon(name: string): string {
  const icons: Record<string, string> = {
    flights: "✈️",
    hotels: "🏨",
    food: "🍽️",
    dining: "🍽️",
    activities: "🎭",
    transport: "🚗",
    shopping: "🛍️",
    other: "📦",
  };
  return icons[name.toLowerCase()] || "💰";
}

// ---------------------------------------------------------------------------
// Fatigue mapper
// ---------------------------------------------------------------------------

function mapFatigue(f: FatigueSummary | undefined): FatigueIndex {
  if (!f) {
    return {
      score: 0, level: "Chill",
      breakdown: {}, recommendations: [],
      lastUpdated: new Date().toISOString(),
    };
  }

  const breakdown: Record<string, FatigueComponent> = {};
  if (f.breakdown) {
    for (const [key, comp] of Object.entries(f.breakdown)) {
      breakdown[key] = {
        score: comp.score,
        weight: comp.weight,
        description: comp.description,
      };
    }
  }

  // Map factor contributions from backend
  const factorContributions: FactorContribution[] | undefined = f.factor_contributions?.map(
    (fc) => ({
      name: fc.name,
      label: fc.label,
      score: fc.score,
      weight: fc.weight,
      weighted: fc.weighted,
      description: fc.description,
    })
  );

  return {
    score: f.score,
    level: mapFatigueLevel(f.level),
    breakdown,
    recommendations: f.recommendations || [],
    lastUpdated: new Date().toISOString(),
    factorContributions,
  };
}

function mapFatigueLevel(level: string): FatigueLevel {
  switch (level.toLowerCase()) {
    case "chill":
    case "low":
    case "light":
      return "Chill";
    case "balanced":
    case "moderate":
      return "Balanced";
    case "packed":
    case "high":
    case "heavy":
    case "severe":
    case "extreme":
      return "Packed";
    default:
      return "Chill";
  }
}

// ---------------------------------------------------------------------------
// Itinerary mapper
// ---------------------------------------------------------------------------

function mapItinerary(days: DaySummary[] | undefined, tripCurrency: string): ItineraryDay[] {
  if (!days || days.length === 0) {
    return [];
  }

  return days.map((day) => ({
    id: `day-${day.day_number}`,
    date: day.date,
    dayNumber: day.day_number,
    location: day.location,
    activities: (day.activities || []).map((a) => mapActivity(a, tripCurrency)),
    // The jet lag the planner gave these days when it built the trip, now read
    // back from core, which works it out from the stored flights (JAR-1763).
    ...(day.tz_shift_hours ? { tzShift: { hours: day.tz_shift_hours, daysSince: day.days_since_shift ?? 0 } } : {}),
  }));
}

export function mapActivity(a: ActivitySummary, tripCurrency: string): Activity {
  return {
    id: a.id,
    name: a.name,
    type: mapActivityType(a.activity_type || a.type),
    time: a.time,
    duration: a.duration || undefined,
    location: {
      name: a.location || "",
      // A geocoded stop's coordinates become a map marker (mapView.ts) and the
      // per-stop map button, day-flow map, journal map and Google Maps link
      // (JAR-2343). `?? undefined` keeps a real 0 but maps absent/null to no
      // coordinates — never a 0,0 in the ocean.
      lat: a.latitude ?? undefined,
      lng: a.longitude ?? undefined,
    },
    cost: a.cost,
    // Core carries this since core#1057 (JAR-2411, deployed 2026-10-02), so a
    // cold load keeps the stop's currency from here. When core omits it on a
    // priced stop (a legacy row saved before that deploy), stamp the trip's
    // currency (JAR-2571), so costCountsIn's "absent means the trip's currency"
    // assumption (JAR-2409 box 3) holds by construction and the next save does
    // not re-stamp a different currency onto it. A costless stop keeps none, and
    // withWhatTheReadBackOmits still repairs a just-saved pre-deploy stop.
    costCurrency: a.currency || (a.cost != null ? tripCurrency : undefined),
    booked: a.booked,
    flight: flightOf(a),
    fatigueImpact: mapFatigueImpact(a.fatigue_impact),
    notes: a.notes,
    providerUrl: a.link_url || undefined,
    linkKind: a.link_kind,
    locked: a.locked,
    lockReason: a.lock_reason,
    lockType: a.lock_type,
    // Who's going, by member id (JAR-2014). Empty/absent means everyone.
    going: a.going ?? [],
  };
}

/** Which flight a flight stop is, from core's answer (JAR-3122). Core needs the
 *  number and both airports on a confirmed flight, so a stop answered with
 *  fewer is one it holds as a planned flight, and has none here. */
function flightOf(a: ActivitySummary): Activity["flight"] {
  if (!a.flight_number || !a.departure_airport || !a.arrival_airport) return undefined;
  return { number: a.flight_number, airline: a.airline ?? "", from: a.departure_airport, to: a.arrival_airport };
}

function mapActivityType(type: string): Activity["type"] {
  const valid: Activity["type"][] = [
    "flight", "transport", "accommodation", "restaurant",
    "attraction", "shopping", "rest", "wellness",
    "entertainment", "tour", "other",
  ];
  if (valid.includes(type as Activity["type"])) {
    return type as Activity["type"];
  }
  // Make an unmapped wire value visible instead of silently bucketing it as
  // "other" — a new/unforwarded backend activity_type should be noticed, not
  // hidden (JAR-449). warnOnce dedupes per distinct value.
  if (type) warnOnce(`[mappers] unmapped activity type "${type}", bucketed as "other"`);
  return "other";
}

function mapFatigueImpact(impact: number): FatigueImpact {
  if (impact <= 3) return "chill";
  if (impact <= 6) return "balanced";
  return "packed";
}

// ---------------------------------------------------------------------------
// Collaborator mapper
// ---------------------------------------------------------------------------

function mapCollaborators(collabs: CollaboratorSummary[] | undefined): Collaborator[] {
  if (!collabs || collabs.length === 0) {
    return [];
  }

  return collabs.map((c) => ({
    id: c.id,
    name: c.email.split("@")[0],
    email: c.email,
    permission: c.permission as "view" | "edit" | "full",
    status: c.status as "pending" | "accepted" | "declined",
  }));
}

// ---------------------------------------------------------------------------
// Stats mapper
// ---------------------------------------------------------------------------

function mapStats(s: TripStatsSummary | undefined): TripStats {
  if (!s) {
    return { restaurants: 0, attractions: 0, flights: 0, photos: 0 };
  }
  return {
    restaurants: s.restaurants || 0,
    attractions: s.attractions || 0,
    flights: s.flights || 0,
    photos: s.photos || 0,
  };
}

// ---------------------------------------------------------------------------
// Weather mapper
// ---------------------------------------------------------------------------

function mapWeather(w: WeatherSummary | undefined): Weather | undefined {
  if (!w) return undefined;

  return {
    temperature: w.temperature,
    feelsLike: w.feels_like,
    condition: w.condition as Weather['condition'],
    humidity: w.humidity,
    windSpeed: w.wind_speed,
    icon: w.icon,
    forecast: (w.forecast || []).map((f) => ({
      day: f.day,
      high: f.high,
      low: f.low,
      icon: f.icon,
    })),
  };
}

// ---------------------------------------------------------------------------
// Property detail mapper
// ---------------------------------------------------------------------------

/** A stay's cost, from the list answer: core serves it there and not on the
 *  detail answer mapProperty reads. Absent from a core that predates it. */
export function mapStayCost(stay: Pick<AccommodationResponse, 'cost_minor' | 'cost_currency' | 'cost_source'>): StayCost | undefined {
  if (stay.cost_minor === undefined || !stay.cost_source) return undefined;
  return { minor: stay.cost_minor, ...(stay.cost_currency ? { currency: stay.cost_currency } : {}), source: stay.cost_source };
}

export function mapProperty(detail: PropertyDetailResponse | null | undefined): Property {
  if (!detail) {
    return {
      id: "", name: "", type: "", address: "", phone: "", website: "",
      rating: 0, reviewCount: 0, image: "",
      amenities: [],
      roomService: { available: false, hours: "", menu: [] },
      dining: [], services: [],
      checkIn: "", checkOut: "",
      location: { lat: 0, lng: 0, nearby: [] },
    };
  }

  const amenities = (detail.amenities || []).map((a) => ({
    id: a.id,
    name: a.name,
    icon: a.icon || "",
    category: (a.category || "services") as "essentials" | "wellness" | "dining" | "services" | "policies",
  }));

  return {
    id: detail.id,
    name: detail.name,
    type: detail.type,
    address: detail.address || "",
    phone: detail.phone || "",
    website: detail.website || "",
    rating: detail.rating || 0,
    reviewCount: detail.review_count,
    // A stay's photo is drawn from our API's copy, never from the hotel's host,
    // so no viewer's address reaches the hotel (JAR-2240, Brent 2026-09-29).
    // It is the only one: a stay keeps one photo, and the route serves no other,
    // so core's `images` stays unmapped (JAR-2254).
    image: detail.image ? apiUrl(`/media/stay-photo?src=${encodeURIComponent(detail.image)}`) : "",
    amenities,
    // The blob is passed through from core verbatim (jsonb), so its fields can be
    // missing individually; normalize here so "available" can never reach the tab
    // with undefined hours/menu (JAR-2237 review).
    roomService: {
      available: Boolean((detail.room_service as unknown as Property["roomService"])?.available),
      hours: (detail.room_service as unknown as Property["roomService"])?.hours ?? "",
      menu: (detail.room_service as unknown as Property["roomService"])?.menu ?? [],
    },
    dining: (detail.dining as unknown as Property["dining"]) || [],
    services: (detail.services as unknown as Property["services"]) || [],
    // Core sends the stay's DATES as check_in/check_out and its times as
    // check_in_time/check_out_time (accommodations/repository.go). checkIn and
    // checkOut are the times the stay tab shows as "from 15:00"; the dates are
    // the reservation's (JAR-1915).
    checkIn: detail.check_in_time || "",
    checkOut: detail.check_out_time || "",
    ...(detail.check_in && detail.check_out
      ? {
          reservation: {
            confirmation: detail.confirmation_number || "",
            checkInDate: detail.check_in,
            checkOutDate: detail.check_out,
            roomType: detail.room_type || "",
          },
        }
      : {}),
    // Core omits the nearby key when a read found a position but no nearby list
    // (repository builds Location for coords alone; nearby is omitempty), so the
    // field is normalized here like every other optional — a tab that reads
    // location.nearby must never see undefined (JAR-2237 review).
    location: {
      lat: detail.location?.lat ?? 0,
      lng: detail.location?.lng ?? 0,
      nearby: detail.location?.nearby ?? [],
    },
    // Spread rather than defaulted: absent is meaningful here. Core omits it
    // for a stay it does not look up, and a core that predates the field omits
    // it for every stay, and in both cases there is no read to wait on
    // (JAR-2628). Writing `lookup: detail.lookup ?? 'found'` would claim a read
    // succeeded that never ran.
    ...(detail.lookup ? { lookup: detail.lookup } : {}),
  };
}

// ---------------------------------------------------------------------------
// TripList mapper (simpler, no sub-resources)
// ---------------------------------------------------------------------------

import type { TripListItem } from "./trips";

/** Core's word for the caller's access, or undefined for anything else. */
function mapAccess(access: string | undefined): TripAccess | undefined {
  return access === 'owner' || access === 'edit' || access === 'view' ? access : undefined;
}

export function mapTripListItem(item: TripListItem): Trip {
  return {
    id: item.id,
    name: item.name,
    destination: item.destination,
    country: item.country,
    image: item.image_url || "",
    dates: {
      checkin: ymdFrom(item.start_date),
      checkout: ymdFrom(item.end_date),
    },
    status: mapStatus(item.status || "planning") as TripStatus,
    travelers: {
      adults: item.adults_count || 1,
      children: item.children_count || 0,
    },
    travelerNames: item.traveler_names,
    access: mapAccess(item.access),
    fromList: true,
    budget: { total: 0, spent: 0, remaining: 0, currency: "USD", perPerson: 0, categories: [], transactions: [] },
    fatigueIndex: { score: 0, level: "Chill", breakdown: {}, recommendations: [], lastUpdated: new Date().toISOString() },
    itinerary: [],
    collaborators: [],
    stats: { restaurants: 0, attractions: 0, flights: 0, photos: 0 },
    createdAt: item.created_at || new Date().toISOString(),
    updatedAt: item.updated_at || new Date().toISOString(),
  };
}
